// Per-member top offset (px) for the slot-anchored line. The line
// normally starts at the decoration's bottom; this nudges that start.
// Negative pulls the line's start earlier (higher up — useful when the
// line should appear to come out of the decoration's body). Positive
// pushes it later (leaves a gap below the decoration).
// The end of each line is fixed (next member's top, or — for the last
// member — the next section's top) so height auto-compensates.
const LINE_TOP_OFFSETS = [-50, 20, 20];

// Per-member height offset (px). Applied on top of the auto-computed
// height so the line can fall short of, or extend past, its default end
// point. Negative shortens; positive lengthens.
const LINE_HEIGHT_OFFSETS = [50, 0, 180];

// How far past the section's bottom the last line should reach if
// there's no following sibling to anchor against.
const LAST_LINE_FALLBACK_OVERSHOOT = 200;

// Append a `.member-line` element to every row, including the last one.
// The last line extends past this section into the next.
function setupRowLines(section) {
  const members = Array.from(section.querySelectorAll(".animated-line-row"));
  const lines = [];

  members.forEach((member, i) => {
    const decoration = member.querySelector(
      ".member-decoration .animated-line-decoration"
    );
    if (!decoration) return;

    const line = document.createElement("span");
    line.className = "member-line";
    member.appendChild(line);

    const isLast = i === members.length - 1;

    lines.push({
      line,
      member,
      decoration,
      nextMember: isLast ? null : members[i + 1],
      isLast,
      index: i,
      section,
    });
  });

  return lines;
}

function getEndYForLastLine(section) {
  const next = section.nextElementSibling;
  if (next) {
    return next.getBoundingClientRect().top;
  }
  return section.getBoundingClientRect().bottom + LAST_LINE_FALLBACK_OVERSHOOT;
}

function measureLine({
  line,
  member,
  decoration,
  nextMember,
  isLast,
  index,
  section,
}) {
  const memberRect = member.getBoundingClientRect();
  const decoRect = decoration.getBoundingClientRect();

  const endY = isLast
    ? getEndYForLastLine(section)
    : nextMember.getBoundingClientRect().top;

  const topOffset = LINE_TOP_OFFSETS[index] ?? 0;
  const heightOffset = LINE_HEIGHT_OFFSETS[index] ?? 0;
  const startY = decoRect.bottom + topOffset;

  line.style.top = `${startY - memberRect.top}px`;
  line.style.height = `${Math.max(0, endY - startY + heightOffset)}px`;
}

const allLines = [];
for (const section of document.querySelectorAll(".animated-line")) {
  allLines.push(...setupRowLines(section));
}

function measureAll() {
  for (const l of allLines) measureLine(l);
}

measureAll();
window.addEventListener("load", measureAll);
window.addEventListener("resize", measureAll);

function makeRevealObserver(rootMargin) {
  return new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          obs.unobserve(entry.target);
        }
      }
    },
    { threshold: 0, rootMargin }
  );
}

// Lines and rows trigger near the top of the viewport so they draw
// alongside the content as the user scrolls past it.
const lineObserver = makeRevealObserver("0px 0px -40% 0px");

// Decorations trigger as soon as they enter the viewport — and a touch
// before, so they're visible by the time their row's content is in view.
const decorationObserver = makeRevealObserver("0px 0px -20% 0px");

for (const el of document.querySelectorAll(
  ".animated-line .animated-line-row," +
    ".animated-line .animated-line-track," +
    ".animated-line .member-line"
)) {
  lineObserver.observe(el);
}

for (const el of document.querySelectorAll(
  ".animated-line .animated-line-decoration"
)) {
  decorationObserver.observe(el);
}
